import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Table, Badge, Button, ButtonGroup } from "react-bootstrap";
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaUsers,
  FaBuilding,
  FaGlobe,
  FaMapMarkerAlt,
  FaIndustry,
  FaCircle
} from "react-icons/fa";
import {
  getCompany,
  deleteCompany,
} from "../../../features/companies/companiesSlice";
import { Company } from "../../../features/companies/companiesService";
import { CompanyFilters } from "../../../features/companies/companiesService";
import CompanyFormModal from "./CompanyFormModal";
import CompanyDetailModal from "./CompanyDetailModal";
import CompanyUsersModal from "./CompanyUsersModal";
import ConfirmationModal from "../../../components/common/ConfirmationModal";
import { AppDispatch } from "../../../store";
import { formatDate } from "../../../utils/dateUtils";
import { Icon } from "../../../components/common/Icon";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import ClientCompaniesModal from "./CompanyClientCompaniesModal";

interface CompaniesListProps {
  companies: Company[];
  onSort: (field: string) => void;
  getSortIcon: (field: string) => React.ReactNode;
  filters: CompanyFilters;
}

const CompaniesList: React.FC<CompaniesListProps> = ({ 
  companies,
  onSort,
  getSortIcon,
  filters
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showClientCompaniesModal, setShowClientCompaniesModal] =
    useState(false);

  // New: compact action menu (per-row)
  const [openActionCompanyId, setOpenActionCompanyId] = useState<number | null>(null);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target as Node)) {
        setOpenActionCompanyId(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Handler fonksiyonu ekleyin (diğer handler fonksiyonlarının yanına)
  const handleViewClientCompanies = (company: Company) => {
    setSelectedCompany(company);
    setShowClientCompaniesModal(true);
    setOpenActionCompanyId(null);
  };

  // Modal'ı kapatma fonksiyonu ekleyin (diğer close fonksiyonlarının yanına)
  const handleCloseClientCompaniesModal = () => {
    setShowClientCompaniesModal(false);
    // selectedCompany'yi null yapma - modal kendi state'ini yönetecek
  };

  // İşlemleri yönet
  const handleViewDetails = (company: Company) => {
    setSelectedCompany(company);
    setShowDetailModal(true);
  };

  const handleEdit = async (company: Company, e?: React.MouseEvent) => {
    try {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      setSelectedCompany(company);
      setShowEditModal(true);
      setOpenActionCompanyId(null);
    } catch (error) {
      console.error("Error handling edit:", error);
    }
  };

  const handleViewUsers = (company: Company) => {
    setSelectedCompany(company);
    setShowUsersModal(true);
  };

  const handleDeleteClick = (company: Company, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedCompany(company);
    setShowDeleteModal(true);
    setOpenActionCompanyId(null);
  };

  const handleConfirmDelete = () => {
    if (selectedCompany) {
      dispatch(deleteCompany(selectedCompany.id));
    }
    setShowDeleteModal(false);
    setSelectedCompany(null);
  };

  // Modal'ları kapat
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedCompany(null);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedCompany(null);
  };

  const handleCloseUsersModal = () => {
    setShowUsersModal(false);
    setSelectedCompany(null);
  };

  // Şirket tipine göre badge
  const getCompanyTypeBadge = (type: string) => {
    switch (type.toLowerCase()) {
      case "agency":
        return (
          <span className="type-badge type-agency">
            <i className="bi bi-building me-1"></i>
            Agency
          </span>
        );
      case "client":
        return (
          <span className="type-badge type-client">
            <i className="bi bi-people me-1"></i>
            Client
          </span>
        );
      case "single":
        return (
          <span className="type-badge type-single">
            <i className="bi bi-person me-1"></i>
            Individual
          </span>
        );
      default:
        return <span className="type-badge type-default">{t(type)}</span>;
    }
  };

  return (
    <>
      <div className="companies-table-responsive">
        <Table className="companies-premium-table" hover>
          <thead>
            <tr>
              <th className="ps-4" onClick={() => onSort("company_name")} style={{ cursor: 'pointer' }}>
                <div className="d-flex align-items-center">
                  {t("company")}
                  {getSortIcon("company_name")}
                </div>
              </th>
              <th onClick={() => onSort("company_type")} style={{ cursor: 'pointer' }}>
                <div className="d-flex align-items-center">
                  {t("type")}
                  {getSortIcon("company_type")}
                </div>
              </th>
              <th className="d-none d-lg-table-cell" onClick={() => onSort("industry")} style={{ cursor: 'pointer' }}>
                <div className="d-flex align-items-center">
                  {t("industry")}
                  {getSortIcon("industry")}
                </div>
              </th>
              <th className="d-none d-md-table-cell">{t("location")}</th>
              <th onClick={() => onSort("is_active")} style={{ cursor: 'pointer' }}>
                <div className="d-flex align-items-center">
                  {t("status")}
                  {getSortIcon("is_active")}
                </div>
              </th>
              <th className="d-none d-xl-table-cell" onClick={() => onSort("created_at")} style={{ cursor: 'pointer' }}>
                <div className="d-flex align-items-center">
                  {t("created_at")}
                  {getSortIcon("created_at")}
                </div>
              </th>
              <th className="d-none d-xl-table-cell" onClick={() => onSort("updated_at")} style={{ cursor: 'pointer' }}>
                <div className="d-flex align-items-center">
                  {t("updated_at")}
                  {getSortIcon("updated_at")}
                </div>
              </th>
              <th className="text-end pe-4">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company) => (
              <tr
                key={company.id}
                onClick={() => {
                  setSelectedCompany(company);
                  setShowEditModal(true);
                }}
              >
                {/* Company */}
                <td className="ps-4">
                  <div className="d-flex align-items-center gap-3">
                    <div className="company-logo-circle">
                      {company.company_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="d-flex flex-column">
                      <span className="fw-bold text-dark">{company.company_name}</span>
                      <span className="text-muted x-small">ID: #{company.id}</span>
                    </div>
                  </div>
                </td>

                {/* Company Type */}
                <td>
                  {getCompanyTypeBadge(company.company_type)}
                </td>

                {/* Industry */}
                <td className="d-none d-lg-table-cell">
                  <span className="text-muted small">{company.industry || "-"}</span>
                </td>

                {/* Location */}
                <td className="d-none d-md-table-cell">
                  <div className="d-flex align-items-center gap-1 text-muted small">
                    <i className="bi bi-geo-alt"></i>
                    <span>{company.city || company.country || "-"}</span>
                  </div>
                </td>

                {/* Status */}
                <td>
                  <div className={`status-pill ${company.is_active ? 'active' : 'inactive'}`}>
                    <span className="dot"></span>
                    <span>{company.is_active ? t("active") : t("inactive")}</span>
                  </div>
                </td>

                {/* Created At */}
                <td className="d-none d-xl-table-cell">
                  <div className="d-flex flex-column">
                    <span className="text-dark small">{new Date(company.created_at).toLocaleDateString()}</span>
                    <span className="text-muted x-small">{new Date(company.created_at).toLocaleTimeString()}</span>
                  </div>
                </td>

                {/* Updated At */}
                <td className="d-none d-xl-table-cell">
                  <div className="d-flex flex-column">
                    <span className="text-dark small">{new Date(company.updated_at).toLocaleDateString()}</span>
                    <span className="text-muted x-small">{new Date(company.updated_at).toLocaleTimeString()}</span>
                  </div>
                </td>

                {/* Actions */}
                <td className="pe-4">
                  <div className="d-flex justify-content-end gap-2" onClick={e => e.stopPropagation()}>
                    <Button
                      variant="link"
                      className="btn-action-premium edit"
                      onClick={(e) => handleEdit(company, e)}
                      title={t("edit")}
                    >
                      <i className="bi bi-pencil-square"></i>
                    </Button>

                    {company.company_type.toLowerCase() === "agency" && (
                      <Button
                        variant="link"
                        className="btn-action-premium info"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleViewClientCompanies(company);
                        }}
                        title={t("client_companies")}
                      >
                        <i className="bi bi-building"></i>
                      </Button>
                    )}

                    <Button
                      variant="link"
                      className="btn-action-premium danger"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteClick(company);
                      }}
                      title={t("delete")}
                    >
                      <i className="bi bi-trash3"></i>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <style>{`
        .companies-table-responsive {
          overflow-x: auto;
          width: 100%;
        }

        .companies-premium-table {
          margin-bottom: 0;
          vertical-align: middle;
        }

        .companies-premium-table thead th {
          background: #f8fafc;
          padding: 16px 12px;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #64748b;
          border-bottom: 1px solid #edf2f7;
        }

        .companies-premium-table tbody tr {
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .companies-premium-table tbody tr:hover {
          background-color: #f8fafc !important;
        }

        .companies-premium-table tbody td {
          padding: 16px 12px;
          border-bottom: 1px solid #f1f5f9;
        }

        .company-logo-circle {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, var(--brand-primary), #7c3aed);
          color: white;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          box-shadow: 0 4px 10px rgba(var(--brand-primary-rgb), 0.2);
        }

        .status-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .status-pill.active {
          background: #ecfdf5;
          color: #10b981;
        }

        .status-pill.inactive {
          background: #fef2f2;
          color: #ef4444;
        }

        .status-pill .dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: currentColor;
        }

        .btn-action-premium {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border-radius: 8px;
          text-decoration: none;
          transition: all 0.2s ease;
        }

        .btn-action-premium.edit { color: #3b82f6; background: rgba(59, 130, 246, 0.05); }
        .btn-action-premium.info { color: #0ea5e9; background: rgba(14, 165, 233, 0.05); }
        .btn-action-premium.danger { color: #ef4444; background: rgba(239, 68, 68, 0.05); }

        .btn-action-premium:hover {
          transform: scale(1.1);
          filter: brightness(0.9);
        }

        .x-small { font-size: 10px; }

        .type-badge {
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
        }

        .type-agency { background: #eff6ff; color: #3b82f6; }
        .type-client { background: #f0fdf4; color: #10b981; }
        .type-single { background: #f5f3ff; color: #8b5cf6; }
      `}</style>

      {/* Modals */}
      {selectedCompany && showEditModal && (
        <CompanyFormModal
          show={showEditModal}
          onHide={handleCloseEditModal}
          company={selectedCompany}
        />
      )}
      {selectedCompany && showDetailModal && (
        <CompanyDetailModal
          show={showDetailModal}
          onHide={handleCloseDetailModal}
          company={selectedCompany}
        />
      )}
      {selectedCompany && showUsersModal && (
        <CompanyUsersModal
          show={showUsersModal}
          onHide={handleCloseUsersModal}
          company={selectedCompany}
        />
      )}
      {showClientCompaniesModal && selectedCompany && (
        <ClientCompaniesModal
          show={showClientCompaniesModal}
          onHide={handleCloseClientCompaniesModal}
          agencyCompany={selectedCompany}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
          setSelectedCompany(null);
        }}
        onConfirm={handleConfirmDelete}
        title={t("delete_company")}
        message={`"${selectedCompany?.company_name}" ${t("company_delete_confirmation")}`}
      />
    </>
  );
};

export default CompaniesList;
